import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { LogoLockup } from "@/components/Logo";
import { inputCls } from "@/components/ui-kit";

function Shell({ children }) {
  return (
    <div className="wf-app-bg min-h-screen flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#E5C378]/8 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#4B7BE5]/8 blur-3xl" />
      </div>
      <div className="relative w-full max-w-md wf-card rounded-2xl p-8 wf-fade-up">
        <div className="flex justify-center mb-6"><LogoLockup /></div>
        {children}
        <p className="text-center text-[10px] text-[#6E7D97] italic mt-6">
          "At the right time, I, the LORD, will make it happen." — Isaiah 60:22
        </p>
      </div>
    </div>
  );
}

function GoogleButton() {
  const { loginWithGoogle } = useAuth();
  return (
    <button
      type="button" onClick={loginWithGoogle} data-testid="google-login-button"
      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white text-[#0A0E1A] font-semibold text-sm hover:bg-gray-100 transition-colors"
    >
      <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.1 36.3 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>
      Continue with Google
    </button>
  );
}

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { await login(email, password); nav("/dashboard"); }
    catch (e) { setErr(apiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <Shell>
      <h1 className="font-display text-2xl font-bold text-center text-[#F7F5F0]">Welcome back</h1>
      <p className="text-center text-sm text-[#8A97AC] mb-6">Prepare. Serve. Worship.</p>
      <form onSubmit={submit} className="space-y-3">
        <input data-testid="login-email" className={inputCls} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <input data-testid="login-password" className={inputCls} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        {err && <div className="text-sm text-rose-400" data-testid="login-error">{err}</div>}
        <button data-testid="login-submit" disabled={loading} className="w-full py-2.5 rounded-xl wf-gold-btn font-semibold text-sm disabled:opacity-60">
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <div className="flex items-center gap-3 my-4"><div className="flex-1 h-px bg-white/10" /><span className="text-xs text-[#6E7D97]">or</span><div className="flex-1 h-px bg-white/10" /></div>
      <GoogleButton />
      <div className="flex items-center justify-between mt-5 text-sm">
        <Link to="/forgot-password" className="text-[#8A97AC] hover:text-[#E5C378]">Forgot password?</Link>
        <Link to="/register" className="text-[#E5C378] hover:underline" data-testid="go-register">Create account</Link>
      </div>
      <div className="mt-4 text-[11px] text-[#6E7D97] text-center bg-[#0B101D] border border-white/10 rounded-lg py-2 px-3">
        Demo: <span className="text-[#B8C1D1]">zayne@worshipflow.demo</span> / <span className="text-[#B8C1D1]">Worship123!</span>
      </div>
    </Shell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await register(email, password, name); nav("/dashboard"); }
    catch (e) { setErr(apiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  return (
    <Shell>
      <h1 className="font-display text-2xl font-bold text-center text-[#F7F5F0]">Create your account</h1>
      <p className="text-center text-sm text-[#8A97AC] mb-6">Join your worship team's workspace.</p>
      <form onSubmit={submit} className="space-y-3">
        <input data-testid="register-name" className={inputCls} placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} required />
        <input data-testid="register-email" className={inputCls} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <input data-testid="register-password" className={inputCls} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
        {err && <div className="text-sm text-rose-400" data-testid="register-error">{err}</div>}
        <button data-testid="register-submit" disabled={loading} className="w-full py-2.5 rounded-xl wf-gold-btn font-semibold text-sm disabled:opacity-60">
          {loading ? "Creating…" : "Create account"}
        </button>
      </form>
      <div className="flex items-center gap-3 my-4"><div className="flex-1 h-px bg-white/10" /><span className="text-xs text-[#6E7D97]">or</span><div className="flex-1 h-px bg-white/10" /></div>
      <GoogleButton />
      <p className="text-center text-sm text-[#8A97AC] mt-5">Already have an account? <Link to="/login" className="text-[#E5C378] hover:underline">Sign in</Link></p>
    </Shell>
  );
}

export function Forgot() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); } catch (e) {}
    setSent(true); setLoading(false);
  };
  return (
    <Shell>
      <h1 className="font-display text-2xl font-bold text-center text-[#F7F5F0]">Reset password</h1>
      {sent ? (
        <p className="text-center text-sm text-[#8A97AC] my-6" data-testid="forgot-confirm">
          If that email is registered, a reset link has been sent. Check your inbox.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-3 mt-6">
          <p className="text-sm text-[#8A97AC] text-center mb-2">Enter your email and we'll send a reset link.</p>
          <input data-testid="forgot-email" className={inputCls} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <button data-testid="forgot-submit" disabled={loading} className="w-full py-2.5 rounded-xl wf-gold-btn font-semibold text-sm disabled:opacity-60">
            {loading ? "Sending…" : "Send reset link"}
          </button>
        </form>
      )}
      <p className="text-center text-sm mt-5"><Link to="/login" className="text-[#E5C378] hover:underline">Back to sign in</Link></p>
    </Shell>
  );
}

export function Reset() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset. Please sign in.");
      nav("/login");
    } catch (e) { setErr(apiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  return (
    <Shell>
      <h1 className="font-display text-2xl font-bold text-center text-[#F7F5F0]">Set a new password</h1>
      <form onSubmit={submit} className="space-y-3 mt-6">
        <input data-testid="reset-password" className={inputCls} type="password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
        {err && <div className="text-sm text-rose-400" data-testid="reset-error">{err}</div>}
        <button data-testid="reset-submit" disabled={loading || !token} className="w-full py-2.5 rounded-xl wf-gold-btn font-semibold text-sm disabled:opacity-60">
          {loading ? "Saving…" : "Reset password"}
        </button>
      </form>
      <p className="text-center text-sm mt-5"><Link to="/login" className="text-[#E5C378] hover:underline">Back to sign in</Link></p>
    </Shell>
  );
}
